import { Id } from "@supernova-studio/model";
import { DTOFileReference } from "../files";
import { DTOForgeFigmaNode } from "../forge";
import { DTOBillingUsageSummary } from "../workspaces";
import z from "zod";
//
// Enums
//
export const DTOThreadSubjectType = z.enum(["ForgeDocument", "ForgeFeature"]);
export const DTOThreadAgentType = z.enum([
    "Ask",
    "Document",
    "Prototype",
    "ReleaseNotes",
]);
export const DTOThreadPromptState = z.enum(["Success", "Timeout", "Error"]);
//
// Data model
//
export const DTOThread = z.object({
    id: z.string(),
    liveblocksRoomId: z.string(),
    defaultAgentType: DTOThreadAgentType,
    subjectId: z.string(),
    subjectType: DTOThreadSubjectType,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DTOThreadMessageUserSender = z.object({
    type: z.literal("User"),
    userId: z.string(),
});
export const DTOThreadMessageAgentSender = z.object({
    type: z.literal("Agent"),
    agentType: DTOThreadAgentType,
});
export const DTOThreadMessageSystemSender = z.object({
    type: z.literal("System"),
    onBehalfOfUserId: z.string(),
});
export const DTOThreadMessageSender = z.discriminatedUnion("type", [
    DTOThreadMessageUserSender,
    DTOThreadMessageAgentSender,
    DTOThreadMessageSystemSender,
]);
export const DTOThreadReaction = z.object({
    messageId: Id,
    userId: z.string(),
    emoji: z.string(),
    createdAt: z.string(),
});
export const DTOThreadMessageAttachments = z.object({
    iterationId: Id.optional(),
    files: DTOFileReference.array().optional(),
    templateId: z.string().optional(),
    figmaNodes: DTOForgeFigmaNode.array().optional(),
});
export const DTOThreadMessageAnnotation = z.object({
    prompt: z.string(),
    elements: z.array(z.object({
        xpath: z.string(),
        line: z.string(),
    })),
});
export const DTOThreadMessageAnnotations = z.array(DTOThreadMessageAnnotation);
export const DTOThreadMessage = z.object({
    id: Id,
    threadId: z.string(),
    /**
     * Describes where the message came from
     */
    sender: DTOThreadMessageSender,
    /**
     * Content of the message
     */
    body: z.string(),
    /**
     * Indicates if the message was sent in the agentic mode, if so this message will cause an
     * AI agent to generate a response and perform an action within the feature
     */
    isPrompt: z.boolean().optional(),
    /**
     * Indicates if the message is an attempt to retry agent message. Only available for agent messages.
     */
    isRetry: z.boolean().optional(),
    /**
     * Indicates if the sender requested agent to reply in a thread
     */
    startsNewThread: z.boolean().optional(),
    /**
     * If defined, this message is considered to be a reply in a thread under parent message id
     */
    parentMessageId: Id.optional(),
    /**
     * Link agent response object describing current state of
     */
    agentResponseTrackerId: Id.optional().nullable(),
    attachments: DTOThreadMessageAttachments.optional(),
    annotations: DTOThreadMessageAnnotations.optional(),
    /**
     * If defined, this message is considered to be a reply to different message
     */
    replyToMessageId: Id.optional(),
    promptMetadata: z.record(z.string(), z.any()).optional(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
});
export const DTOThreadMessagePreview = DTOThreadMessage.pick({
    id: true,
    threadId: true,
    sender: true,
    isPrompt: true,
    createdAt: true,
    updatedAt: true,
});
export const DTOThreadAgentResponseTracker = z.object({
    id: Id,
    currentBody: z.string().default(""),
});
//
// Payloads
//
export const DTOThreadMessageAttachmentsCreateInput = z.object({
    iterationId: Id.optional(),
    fileIds: z.string().array().optional(),
    figmaNodeIds: z.string().array().optional(),
    templateId: z.string().optional(),
});
export const DTOThreadMessageCreateInput = DTOThreadMessage.pick({
    id: true,
    body: true,
    isPrompt: true,
    startsNewThread: true,
    parentMessageId: true,
    promptMetadata: true,
    annotations: true,
}).extend({
    attachments: DTOThreadMessageAttachmentsCreateInput.optional(),
});
export const DTOThreadMessageFinalizeInput = z.object({
    messageId: Id,
    agentMessageBody: z.string().optional(),
    promptState: DTOThreadPromptState.optional(),
    billingUsageSummary: DTOBillingUsageSummary.optional(),
});
export const DTOThreadMessageRetryInput = z.object({
    agentMessageId: Id,
    runtimeError: z.string().optional(),
    /**
     * When `true`, the retry message will be sent to a thread started by the user's message
     * Useful when user retries a message created in the main thread, but expects the retry to be sent as a thread reply
     */
    shouldReplyInThread: z.boolean().optional(),
    message: DTOThreadMessage.pick({ id: true, body: true }).optional(),
});
export const DTOThreadMessageUpdateInput = DTOThreadMessage.pick({
    id: true,
}).merge(z
    .object({
    body: DTOThreadMessage.shape.body,
    attachments: DTOThreadMessage.shape.attachments,
    agentResponseTrackerId: DTOThreadMessage.shape.agentResponseTrackerId.nullable(),
})
    .partial());
export const DTOThreadReactionCreateInput = z.object({
    messageId: Id,
    emoji: z.string(),
});
export const DTOThreadReactionDeleteInput = z.object({
    messageId: Id,
    emoji: z.string(),
});
//
// Responses
//
export const DTOThreadMessageResponse = z.object({
    message: DTOThreadMessage,
});
export const DTOThreadReactionResponse = z.object({
    reaction: DTOThreadReaction,
});
export const DTOThreadMessageListResponse = z.object({
    messages: DTOThreadMessage.array(),
    reactions: DTOThreadReaction.array(),
    lastSeenMessageId: z.string().optional(),
});
//
// Events
//
export const DTOThreadEventMessagesSent = z.object({
    type: z.literal("MessagesSent"),
    data: DTOThreadMessage.array(),
});
export const DTOThreadEventMessagesUpdated = z.object({
    type: z.literal("MessagesUpdated"),
    data: DTOThreadMessage.array(),
});
export const DTOThreadEventReactionsSent = z.object({
    type: z.literal("ReactionsSent"),
    data: DTOThreadReaction.array(),
});
export const DTOThreadEventReactionsDeleted = z.object({
    type: z.literal("ReactionsDeleted"),
    data: DTOThreadReaction.array(),
});
export const DTOThreadEvent = z.discriminatedUnion("type", [
    DTOThreadEventMessagesSent,
    DTOThreadEventMessagesUpdated,
    DTOThreadEventReactionsSent,
    DTOThreadEventReactionsDeleted,
]);
